import React from 'react';

import Link from 'next/link';

import { ArrowUpRight } from 'lucide-react';

import { CtfRichText } from '@/modules/common/components/CtfRichText';
import Image from '@/modules/common/components/Image';

import { AuthorFieldsFragment } from '@/gql/__generated/graphql.types';

import { PinterestIcon } from './icons/PinterestIcon';
import { ThreadsIcon } from './icons/ThreadsIcon';
import { XIcon } from './icons/XIcon';

type BlogAuthorSectionProps = {
  author: AuthorFieldsFragment;
};

export const BlogAuthorSection = ({ author }: BlogAuthorSectionProps) => {
  const showFollowSection = author.twitter || author.threads || author.pinterest;

  return (
    <section className="gap-6 rounded-3xl bg-gray-75 px-4 py-4 lg:flex lg:px-20 lg:py-12">
      {author.avatar && (
        <div className="min-w-24">
          <Image
            src={author.avatar?.url ?? ''}
            className="block h-24 w-24 rounded-xl object-contain"
            alt={author.avatar?.title ?? ''}
          />
        </div>
      )}
      <div>
        <h2 className="mb-4 text-2xl font-bold">Meet the author, {author.name}</h2>

        <div className="pb-5">
          {author.description?.json && <CtfRichText {...author.description} />}
        </div>

        <Link href={`/blog/author/${author?.slug}`} className="flex font-bold">
          More from {author.name} <ArrowUpRight strokeWidth={1} className="font-thin" />
        </Link>

        {showFollowSection && (
          <section className="mt-5 flex gap-7 border-t border-gray-200 pt-6">
            <h2 className="font-bold">Follow on:</h2>
            {author.twitter && (
              <a href={author.twitter} aria-label="Twitter">
                <XIcon />
              </a>
            )}
            {author.threads && (
              <a href={author.threads} aria-label="Threads">
                <ThreadsIcon />
              </a>
            )}
            {author.pinterest && (
              <a href={author.pinterest} aria-label="Pinterest">
                <PinterestIcon />
              </a>
            )}
          </section>
        )}
      </div>
    </section>
  );
};
